import {
  ArrowUpRight,
  ChatCircleDots,
  Coins,
  Gavel,
  Handshake,
  IdentificationCard,
  ListChecks,
  Tag,
  Users,
} from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

import { trpc } from '@/lib/trpc/client';

export const Route = createFileRoute('/_admin/admin/')({
  component: AdminDashboardPage,
});

interface KpiCardProps {
  label: string;
  value: React.ReactNode;
  href: string;
  icon: React.ReactNode;
}

function KpiCard({ label, value, href, icon }: KpiCardProps): React.ReactElement {
  return (
    <Link
      to={href}
      className="group border-border bg-card hover:border-primary/40 rounded-2xl border p-4 transition-colors"
    >
      <div className="mb-3.5 flex items-center justify-between">
        <span className="bg-primary/10 text-primary grid size-10 shrink-0 place-items-center rounded-xl">
          {icon}
        </span>
        <ArrowUpRight
          className="text-muted-foreground group-hover:text-primary size-4 shrink-0 transition-colors"
          weight="bold"
        />
      </div>
      <p className="text-muted-foreground text-sm">{label}</p>
      <p className="mt-2.5 font-mono text-[31px] leading-[0.95] font-bold tracking-[-0.05em] tabular-nums">
        {value}
      </p>
    </Link>
  );
}

function AdminDashboardPage(): React.ReactElement {
  const { data: kpis, isLoading } = trpc.admin.kpis.useQuery();
  const aiCost = trpc.admin.aiCostTotal.useQuery();

  const value = (v: number | undefined): React.ReactNode =>
    isLoading || v === undefined ? '—' : v;

  const euroFormatter = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  });

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <KpiCard
        label="Vérifications en attente"
        value={value(kpis?.pendingVerifications)}
        href="/admin/verifications"
        icon={<IdentificationCard className="size-5" weight="regular" />}
      />
      <KpiCard
        label="Litiges N2 en attente"
        value={value(kpis?.pendingDisputesLvl2)}
        href="/admin/disputes"
        icon={<Gavel className="size-5" weight="regular" />}
      />
      <KpiCard
        label="Codes promo actifs"
        value={value(kpis?.activePromoCodes)}
        href="/admin/promos"
        icon={<Tag className="size-5" weight="regular" />}
      />
      <KpiCard
        label="Signalements en attente"
        value={value(kpis?.pendingMessageReports)}
        href="/admin/moderation"
        icon={<ChatCircleDots className="size-5" weight="regular" />}
      />
      <KpiCard
        label="Grants Early Adopter"
        value={value(kpis?.activeEarlyAdopterGrants)}
        href="/admin/grants"
        icon={<ListChecks className="size-5" weight="regular" />}
      />
      <KpiCard
        label="Utilisateurs suspendus"
        value={value(kpis?.suspendedUsers)}
        href="/admin/users"
        icon={<Users className="size-5" weight="regular" />}
      />
      <KpiCard
        label="Anti-fraude parrainage"
        value={value(kpis?.pendingReferralFraudChecks)}
        href="/admin/referrals"
        icon={<Handshake className="size-5" weight="regular" />}
      />
      <KpiCard
        label="Coût IA total"
        value={
          aiCost.isLoading || aiCost.data === undefined
            ? '—'
            : euroFormatter.format(aiCost.data.totalEUR)
        }
        href="/admin/ai-costs"
        icon={<Coins className="size-5" weight="regular" />}
      />
    </div>
  );
}
